import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import * as ProjectAPI from 'api/project-api';
import { Button, Form, App } from 'antd';
import FormModal from 'components/FormModal';
import { useSession } from 'components/Authenticated';
import { useBeeyTutorial } from 'screens/DashboardScreen/beeyTutorial';
import { txt } from 'libs/i18n';
import { BeeyLocale } from 'libs/locales/locale';
import { SubtitlesFormatSchema, subtitlesDefaults } from 'api/settings/user-settings';
import { downloadBlobAsFile } from '../exports';
import { SUBTITLE_LENGTH } from '../CaptionReviewModal';
import DownloadSubtitles from './DownloadSubtitles';
import { useEditorContext } from '../../contexts/projectSignal';
import { canUseSubtitleFormat } from './format-claims';
const DownloadSubtitlesModal = ({ visible, onCancel, enqueueProjectUpdate, isCaptionMode, captions, onSwitchToCaptionMode, }) => {
    var _a, _b, _c, _d;
    const { session, updateUserSettings } = useSession();
    const hasClaim = (claim) => session.login.hasClaim(claim);
    const savedExportSettings = session.login.user.settings.subtitlesExport;
    const savedSubtitlesSettings = session.login.user.settings.subtitles;
    const { tutorialContinue, tutorialGoNext, retrieveTutorialState } = useBeeyTutorial();
    const isTutorialRunning = retrieveTutorialState() === 'running';
    const { projectSignal } = useEditorContext();
    const [form] = Form.useForm();
    const [additionalInfo, setAdditionalInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const { message } = App.useApp();
    const subtitleLength = (_a = captions.parameters) === null || _a === void 0 ? void 0 : _a.maxLineLength;
    const subsFormat = Form.useWatch('subtitlesFormat', form);
    const highlightingMode = Form.useWatch('highlightingMode', form);
    const unhighlightedColor = (_b = Form.useWatch('unHighlightedColor', form)) !== null && _b !== void 0 ? _b : subtitlesDefaults.unHighlightedColor;
    const allSubtitlesAllowed = session.login.hasClaim('subtitles:all');
    const selectInitialValue = () => {
        return SubtitlesFormatSchema.values.find((format) => canUseSubtitleFormat(hasClaim, format, allSubtitlesAllowed));
    };
    const initialFormat = canUseSubtitleFormat(hasClaim, savedExportSettings.subtitlesFormat, allSubtitlesAllowed)
        ? savedExportSettings.subtitlesFormat
        : selectInitialValue();
    const initialValues = Object.assign(Object.assign({}, savedExportSettings), { subtitlesFormat: initialFormat, highlightingMode: (_c = savedSubtitlesSettings.highlightingMode) !== null && _c !== void 0 ? _c : 'None', unHighlightedColor: (_d = savedSubtitlesSettings.unHighlightedColor) !== null && _d !== void 0 ? _d : '#ffffff' });
    const createInfoMessage = (subtitlesFormat) => {
        if (subtitlesFormat === 'stl' || subtitlesFormat === 'ttml') {
            return `${txt('format')} ${subtitlesFormat} ${txt('unsupportedFormatting')}`;
        }
        if (subtitlesFormat === 'srt_FB') {
            return txt('noFormatting');
        }
        return null;
    };
    const updateAdditionalInfo = () => {
        var _a;
        if (!isCaptionMode) {
            return;
        }
        setAdditionalInfo(createInfoMessage((_a = form.getFieldValue('subtitlesFormat')) !== null && _a !== void 0 ? _a : initialValues.subtitlesFormat));
    };
    useEffect(() => {
        if (visible === true) {
            tutorialContinue();
            void updateAdditionalInfo();
        }
    }, [visible]);
    const handleSubtitleError = (error) => {
        switch (error) {
            case 'invalid-file-format':
                void message.error(txt('invalidExportFormat'));
                break;
            case 'max-sub-length':
                void message.error(`${txt('maxSubtitleLineLength')} ${SUBTITLE_LENGTH.max}`);
                break;
            case 'min-sub-length':
                void message.error(`${txt('minSubtitleLineLength')} ${SUBTITLE_LENGTH.min}`);
                break;
            case 'negative-sub-length':
                void message.error(txt('integerSubtitleLineLength'));
                break;
            default:
                void message.error(txt('subtitleExportMessageError'));
                break;
        }
    };
    const processSubtitles = async (formValues) => {
        setIsLoading(true);
        try {
            const { maxLineLength, speakerSignPlacement, pauseBetweenCaptions, autofillPauseBetweenCaptionsMs, formatting, upperCaseAllText, } = formValues;
            const projectLanguage = await ProjectAPI.getProjectLanguage(session.connection, projectSignal.value.id);
            const language = projectLanguage !== null ? projectLanguage : BeeyLocale.const('en-US');
            const isRightToLeft = language.isRightToLeft();
            const subtitlesFormat = subsFormat === 'srt_FB' ? 'srt' : subsFormat;
            const subtitlesName = subsFormat === 'srt_FB' ? `${projectSignal.value.description.name}.${language.code.replace('-', '_')}` : projectSignal.value.description.name;
            const downloadExtension = subtitlesFormat === 'stl_apa' ? 'stl' : subtitlesFormat;
            const result = (isCaptionMode && captions.parameters !== null)
                ? await ProjectAPI.downloadSubtitlesFromLabeled(session.connection, projectSignal.value, subtitlesFormat, (formatting === true || formatting === undefined) ? 'AllowAll' : 'DisableAll', upperCaseAllText !== null && upperCaseAllText !== void 0 ? upperCaseAllText : captions.parameters.upperCaseAllText, captions.parameters.keepInnerLinesStripped, isRightToLeft)
                : await ProjectAPI.downloadSubtitles(session.connection, projectSignal.value, subtitlesFormat, maxLineLength, speakerSignPlacement !== null && speakerSignPlacement !== void 0 ? speakerSignPlacement : subtitlesDefaults.speakerSignPlacement, pauseBetweenCaptions !== null && pauseBetweenCaptions !== void 0 ? pauseBetweenCaptions : subtitlesDefaults.pauseBetweenCaptions, autofillPauseBetweenCaptionsMs === undefined
                    ? subtitlesDefaults.autofillPauseBetweenCaptions
                    : autofillPauseBetweenCaptionsMs / 1000, upperCaseAllText !== null && upperCaseAllText !== void 0 ? upperCaseAllText : false, isRightToLeft);
            if (result.isSuccess()) {
                downloadBlobAsFile(`${subtitlesName}.${downloadExtension}`, result.get());
                setIsLoading(false);
                updateUserSettings({
                    subtitlesExport: {
                        maxLineLength,
                        speakerSignPlacement: speakerSignPlacement !== null && speakerSignPlacement !== void 0 ? speakerSignPlacement : savedExportSettings.speakerSignPlacement,
                        pauseBetweenCaptions: pauseBetweenCaptions !== null && pauseBetweenCaptions !== void 0 ? pauseBetweenCaptions : savedExportSettings.pauseBetweenCaptions,
                        autofillPauseBetweenCaptionsMs: autofillPauseBetweenCaptionsMs !== null && autofillPauseBetweenCaptionsMs !== void 0 ? autofillPauseBetweenCaptionsMs : savedExportSettings.autofillPauseBetweenCaptionsMs,
                        subtitlesFormat: subsFormat,
                        upperCaseAllText: upperCaseAllText !== null && upperCaseAllText !== void 0 ? upperCaseAllText : savedExportSettings.upperCaseAllText,
                        formatting: formatting !== null && formatting !== void 0 ? formatting : savedExportSettings.formatting,
                    },
                    subtitles: Object.assign(Object.assign({}, savedSubtitlesSettings), { highlightingMode, unHighlightedColor: unhighlightedColor }),
                });
                onCancel();
            }
            else {
                handleSubtitleError(result.err());
            }
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleConfirm = async (formValues) => {
        await enqueueProjectUpdate(async (initialProject) => {
            await processSubtitles(formValues);
            return initialProject;
        });
    };
    const onModalOk = () => {
        form.submit();
        tutorialGoNext();
    };
    return (_jsx(FormModal, { muteShortcuts: ['Tab', 'Shift+Tab'], title: txt('downloadSubtitles'), className: "beey-tutorial-step-format", open: visible, footer: [
            _jsx(Button, { type: "default", onClick: onCancel, disabled: isTutorialRunning, children: txt('cancel') }, "back"),
            _jsx(Button, { type: "primary", htmlType: "submit", onClick: onModalOk, disabled: isLoading, loading: isLoading, children: txt('downloadVerb') }, "submit"),
        ], onCancel: onCancel, width: 560, destroyOnClose: true, closable: !isTutorialRunning, keyboard: !isTutorialRunning, children: _jsx(Form, { form: form, initialValues: initialValues, requiredMark: false, onFinish: handleConfirm, onValuesChange: updateAdditionalInfo, labelAlign: "left", labelWrap: true, labelCol: { span: 12 }, colon: false, children: _jsx(DownloadSubtitles, { isCaptionMode: isCaptionMode, subtitleLength: subtitleLength, onSwitchToCaptionMode: onSwitchToCaptionMode, form: form, additionalInfo: additionalInfo }) }) }));
};
export default DownloadSubtitlesModal;
